/**
 * Fabric / colour preview card on the PDP variant picker.
 * A/B: MC_PDP_FABRICCOLOURPREVIEW_LB24
 *
 * VariantPicker.selectVariant() dispatches "variant:change" on the product form with the resolved variant
 * (theme.js), which is the same event <variant-inventory> already listens to. The PDP does not re-render its
 * section on a variant change, so a listener bound in connectedCallback is enough — no delegation needed.
 *
 * The card is driven by the colour option VALUE rather than the variant id: the fabric close-up belongs to a
 * colour, and the same colour is shared by every size / frame combination.
 */
if (!window.customElements.get('fabric-colour-preview')) {
  class FabricColourPreview extends HTMLElement {
    connectedCallback() {
      this._samples = this._readSamples();
      this._image = this.querySelector('.fcp24-card__image');
      this._title = this.querySelector('.fcp24-card__title');
      this._caption = this.querySelector('.fcp24-card__caption');
      this._suffix = this.getAttribute('caption-suffix') || '';
      this._optionIndex = parseInt(this.getAttribute('option-position'), 10) - 1;

      this._onVariantChanged = this._onVariantChanged.bind(this);
      this._form = document.forms[this.getAttribute('form')];
      this._form?.addEventListener('variant:change', this._onVariantChanged);

      this._setupZoom();
    }

    disconnectedCallback() {
      this._form?.removeEventListener('variant:change', this._onVariantChanged);
      document.removeEventListener('keydown', this._onZoomKeydown);
      // The dialog lives in <body> once opened, so it does not travel with the element. Put it back rather than
      // delete it: the theme editor disconnects and reconnects the same node, and a deleted dialog would leave the
      // card looking fine with a dead zoom.
      if (this._dialog && this._dialog.parentNode === document.body) {
        this._dialog.hidden = true;
        this.appendChild(this._dialog);
      }
    }

    _readSamples() {
      const holder = this.querySelector('script[type="application/json"]');
      if (!holder) return {};
      try {
        return JSON.parse(holder.textContent) || {};
      } catch (e) {
        // A malformed table must not take the whole picker down with it.
        return {};
      }
    }

    _onVariantChanged(event) {
      const variant = event.detail?.variant;
      if (!variant || !Array.isArray(variant.options)) return;

      const value = variant.options[this._optionIndex];
      const sample = value ? this._samples[value] : null;

      // A colour with no sample photo hides the card rather than keeping the previous colour's fabric on
      // screen, which would be actively misleading.
      if (!sample) {
        this.toggleAttribute('hidden', true);
        return;
      }

      this.toggleAttribute('hidden', false);
      if (this._image && this._image.getAttribute('src') !== sample.src) {
        this._image.src = sample.src;
      }
      if (this._title) this._title.textContent = value;
      if (this._caption) {
        this._caption.textContent = this._suffix ? `${sample.material} · ${this._suffix}` : sample.material;
      }

      this._setZoomSource(sample.zoom, value);
    }

    /* ----------------------------------------------------------------------------------------------------------
     * Zoom
     *
     * The card is 80px; the close-up behind it is worth seeing full size, which is the whole point of showing a
     * fabric at all. Opening is the element's job rather than a shared window event because a page can hold more
     * than one of these cards - quick buy renders the same variant picker.
     * -------------------------------------------------------------------------------------------------------- */

    _setupZoom() {
      this._trigger = this.querySelector('.fcp24-card__zoom');
      this._dialog = this.querySelector('.fcp24-zoom');
      if (!this._trigger || !this._dialog) return;

      // A reconnect must not bind a second set of listeners to the same nodes.
      if (this._zoomBound) return;
      this._zoomBound = true;

      this._dialogImage = this._dialog.querySelector('.fcp24-zoom__image');
      this._dialogCaption = this._dialog.querySelector('.fcp24-zoom__caption');
      this._zoomSrc = this._trigger.getAttribute('data-zoom-src') || '';
      this._zoomLabel = this._title?.textContent.trim() || '';

      this._onZoomKeydown = this._onZoomKeydown.bind(this);
      this._trigger.addEventListener('click', () => this._openZoom());
      this._dialog.querySelector('.fcp24-zoom__close')?.addEventListener('click', () => this._closeZoom());
      // Only a click on the backdrop itself closes; one inside the white box must not.
      this._dialog.addEventListener('click', (event) => {
        if (event.target === this._dialog) this._closeZoom();
      });

      this._syncTrigger();
    }

    _setZoomSource(src, label) {
      this._zoomSrc = src || '';
      this._zoomLabel = label || '';
      this._syncTrigger();

      // A colour switched underneath an open dialog should show the new fabric, not the old one.
      if (this._dialog && !this._dialog.hidden) this._paintDialog();
    }

    _syncTrigger() {
      if (!this._trigger) return;
      // No large image means nothing to enlarge: drop the affordance rather than open an empty box.
      this._trigger.toggleAttribute('data-no-zoom', !this._zoomSrc);
      if (this._zoomSrc) {
        this._trigger.setAttribute('data-zoom-src', this._zoomSrc);
      } else {
        this._trigger.removeAttribute('data-zoom-src');
        if (!this._dialog?.hidden) this._closeZoom();
      }
    }

    /**
     * Assigning the large src straight to the <img> leaves the previous colour's fabric on screen for as long as
     * the new file takes to arrive — the shopper opens the dialog and watches the wrong photo turn into the right
     * one. Clearing the src on close would fix the wrongness and replace it with an empty white panel.
     *
     * So the dialog opens on the thumbnail the card is already showing: it is the right colour, it is in cache,
     * and it paints in the same frame. The large file swaps in behind a blur that lifts once it decodes, which is
     * the same trick the theme plays on gallery placeholders.
     */
    _paintDialog() {
      const image = this._dialogImage;

      if (image && image.dataset.zoomSrc !== this._zoomSrc) {
        image.dataset.zoomSrc = this._zoomSrc;
        image.src = this._image?.currentSrc || this._image?.getAttribute('src') || '';
        image.classList.add('fcp24-zoom__image--loading');

        const full = new Image();
        full.decoding = 'async';
        full.onload = () => {
          // The colour can move on while a 1400px file is in flight; the last one asked for is the one to show.
          if (image.dataset.zoomSrc !== this._zoomSrc) return;
          image.src = this._zoomSrc;
          image.classList.remove('fcp24-zoom__image--loading');
        };
        full.onerror = () => image.classList.remove('fcp24-zoom__image--loading');
        full.src = this._zoomSrc;
      }

      if (image) image.alt = this._zoomLabel;
      if (this._dialogCaption) this._dialogCaption.textContent = this._caption?.textContent || '';
    }

    _openZoom() {
      if (!this._zoomSrc || !this._dialog) return;

      // A position:fixed layer is only fixed to the viewport while no ancestor is transformed. The card sits deep
      // inside the product form, so the dialog is lifted out on first use and stays in <body> from then on.
      if (this._dialog.parentNode !== document.body) document.body.appendChild(this._dialog);

      this._paintDialog();
      this._returnFocusTo = document.activeElement;
      this._dialog.hidden = false;
      document.addEventListener('keydown', this._onZoomKeydown);
      this._dialog.querySelector('.fcp24-zoom__close')?.focus();
    }

    _closeZoom() {
      if (!this._dialog || this._dialog.hidden) return;
      this._dialog.hidden = true;
      document.removeEventListener('keydown', this._onZoomKeydown);
      this._returnFocusTo?.focus?.();
    }

    _onZoomKeydown(event) {
      if (event.key === 'Escape') this._closeZoom();
    }
  }

  window.customElements.define('fabric-colour-preview', FabricColourPreview);
}
